import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Tag, Users, User, AtSign } from 'lucide-react';
import { INACTIVE_CLASSES } from '../mock';

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const hasEmail = (u) => EMAIL_RX.test(u.email || '');
const uniq = (arr) => [...new Set(arr)];

// Address field for member comms: dropdown lists tagged lists first, then member classes, then members;
// any typed email address is kept as an extra recipient. Value = { ids: [user_id], emails: [address] }.
export default function AddressPicker({ users = [], memberClasses = [], classLabel = (k) => k, value, onChange, testId = 'address-picker', placeholder = 'Add a list, member class, member or email address…', emailOnly = false }) {
  const ids = value?.ids || [];
  const emails = value?.emails || [];
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrapRef = useRef(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const inactiveCount = useMemo(() => users.filter((u) => INACTIVE_CLASSES.includes(u.member_class)).length, [users]);
  const pool = useMemo(() => users.filter((u) => (!emailOnly || hasEmail(u)) && (includeInactive || !INACTIVE_CLASSES.includes(u.member_class))), [users, emailOnly, includeInactive]);

  const tags = useMemo(() => uniq(pool.flatMap((u) => u.tags || [])).sort().map((t) => ({ kind: 'tag', key: t, label: t, members: pool.filter((u) => (u.tags || []).includes(t)).map((u) => u.user_id) })), [pool]);
  const classes = useMemo(() => memberClasses.filter((m) => m.key !== 'public').map((m) => ({ kind: 'class', key: m.key, label: classLabel(m.key), members: pool.filter((u) => u.member_class === m.key).map((u) => u.user_id) })).filter((c) => c.members.length > 0), [memberClasses, classLabel, pool]);

  const set = (nextIds, nextEmails) => onChange({ ids: uniq(nextIds), emails: uniq(nextEmails.map((e) => e.trim().toLowerCase()).filter(Boolean)) });
  const addIds = (list) => set([...ids, ...list], emails);
  const removeIds = (list) => set(ids.filter((id) => !list.includes(id)), emails);
  const addEmail = (e) => { const v = e.trim().toLowerCase(); if (!EMAIL_RX.test(v)) return false; set(ids, [...emails, v]); return true; };

  const ql = q.trim().toLowerCase();
  const options = useMemo(() => {
    const match = (s) => !ql || s.toLowerCase().includes(ql);
    const groups = [];
    const t = tags.filter((g) => match(g.label) && !g.members.every((id) => ids.includes(id)));
    if (t.length) groups.push({ title: 'Lists (tags)', icon: Tag, items: t });
    const c = classes.filter((g) => match(g.label) && !g.members.every((id) => ids.includes(id)));
    if (c.length) groups.push({ title: 'Member classes', icon: Users, items: c });
    const m = pool.filter((u) => !ids.includes(u.user_id) && match(`${u.name || ''} ${u.email || ''}`)).slice(0, ql ? 8 : 5).map((u) => ({ kind: 'user', key: u.user_id, label: u.name || u.email, sub: u.email || 'no email', members: [u.user_id] }));
    if (m.length) groups.push({ title: 'Members', icon: User, items: m });
    if (EMAIL_RX.test(ql) && !emails.includes(ql)) groups.push({ title: 'Other address', icon: AtSign, items: [{ kind: 'email', key: ql, label: `Add “${ql}”`, members: [] }] });
    return groups;
  }, [tags, classes, pool, ids, emails, ql]);
  const flat = options.flatMap((g) => g.items);

  useEffect(() => { setHi(0); }, [ql, open]);
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (item) => {
    if (item.kind === 'email') addEmail(item.key); else addIds(item.members);
    setQ(''); setOpen(false);
  };
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, flat.length - 1)); setOpen(true); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      if (!ql) return;
      e.preventDefault();
      if (flat[hi]) pick(flat[hi]); else if (addEmail(ql)) { setQ(''); setOpen(false); }
    } else if (e.key === 'Backspace' && !q) {
      if (emails.length) set(ids, emails.slice(0, -1)); else if (ids.length) set(ids.slice(0, -1), emails);
    } else if (e.key === 'Escape') setOpen(false);
  };
  const onPaste = (e) => {
    const text = e.clipboardData.getData('text');
    const parts = text.split(/[\s,;]+/).filter(Boolean);
    if (parts.length > 1 || EMAIL_RX.test(text.trim())) {
      e.preventDefault();
      const good = parts.map((p) => p.toLowerCase()).filter((p) => EMAIL_RX.test(p));
      const byEmail = new Map(pool.map((u) => [(u.email || '').toLowerCase(), u.user_id]));
      set([...ids, ...good.filter((g) => byEmail.has(g)).map((g) => byEmail.get(g))], [...emails, ...good.filter((g) => !byEmail.has(g))]);
    }
  };

  // Collapse fully-selected groups into one chip (tags first, largest first; skip groups already covered); remaining members shown individually.
  const chips = useMemo(() => {
    let rest = [...ids];
    const out = [];
    const groups = [...[...tags].sort((a, b) => b.members.length - a.members.length), ...[...classes].sort((a, b) => b.members.length - a.members.length)];
    groups.forEach((g) => {
      if (g.members.length && g.members.every((id) => ids.includes(id)) && g.members.some((id) => rest.includes(id))) {
        out.push({ ...g, remove: () => removeIds(g.members) }); rest = rest.filter((id) => !g.members.includes(id));
      }
    });
    rest.forEach((id) => { const u = users.find((x) => x.user_id === id); out.push({ kind: 'user', key: id, label: u ? (u.name || u.email) : id, warn: u && !hasEmail(u), remove: () => removeIds([id]) }); });
    emails.forEach((e) => out.push({ kind: 'email', key: e, label: e, remove: () => set(ids, emails.filter((x) => x !== e)) }));
    return out;
  }, [ids, emails, tags, classes, users]); // eslint-disable-line react-hooks/exhaustive-deps

  const withEmail = users.filter((u) => ids.includes(u.user_id) && hasEmail(u)).length;
  const chipStyle = { tag: { background: 'var(--navy)', color: 'var(--cream)' }, class: { background: 'var(--olive)', color: 'var(--cream)' }, user: { background: 'white', border: '1px solid rgba(0,0,0,0.18)' }, email: { background: 'rgba(154,107,0,0.12)', color: '#6b4c0f' } };
  const Icon = { tag: Tag, class: Users, user: User, email: AtSign };

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <div className="flex flex-wrap gap-1.5 items-center rounded border bg-white px-2 py-1.5 min-h-[40px] cursor-text" style={{ borderColor: 'rgba(0,0,0,0.2)' }} onClick={() => { setOpen(true); wrapRef.current?.querySelector('input')?.focus(); }}>
        {chips.map((c) => { const I = Icon[c.kind]; return (
          <span key={`${c.kind}-${c.key}`} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs font-title" style={chipStyle[c.kind]} title={c.warn ? 'No email address on file' : c.kind === 'tag' ? `List: ${c.label}` : c.kind === 'class' ? `Member class: ${c.label}` : ''} data-testid={`${testId}-chip-${c.kind}-${c.key}`}>
            <I size={11} /> {c.label}{c.members ? ` (${c.members.length})` : ''}{c.warn && <span title="No email">⚠</span>}
            <button type="button" onClick={(e) => { e.stopPropagation(); c.remove(); }} className="rounded-full p-0.5 hover:bg-black/10" aria-label={`Remove ${c.label}`} data-testid={`${testId}-remove-${c.kind}-${c.key}`}><X size={11} /></button>
          </span>); })}
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKey} onPaste={onPaste}
          placeholder={chips.length ? 'Add more…' : placeholder} className="flex-1 min-w-[160px] outline-none text-sm py-0.5 bg-transparent" data-testid={`${testId}-input`} aria-label="Add recipient" />
      </div>
      {open && flat.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border rounded shadow-lg max-h-72 overflow-y-auto text-sm" style={{ borderColor: 'rgba(0,0,0,0.15)' }} data-testid={`${testId}-menu`} role="listbox">
          {options.map((g) => { const GI = g.icon; return (
            <div key={g.title}>
              <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wide font-title flex items-center gap-1" style={{ color: 'var(--olive)' }}><GI size={10} /> {g.title}</div>
              {g.items.map((item) => { const idx = flat.indexOf(item); return (
                <button key={`${item.kind}-${item.key}`} type="button" role="option" aria-selected={idx === hi} onMouseEnter={() => setHi(idx)} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(item)}
                  className="w-full text-left px-3 py-1.5 flex items-center justify-between gap-2" style={idx === hi ? { background: 'rgba(0,0,0,0.05)' } : undefined} data-testid={`${testId}-opt-${item.kind}-${item.key}`}>
                  <span>{item.label}{item.sub && <span className="ml-1 text-xs" style={{ color: 'var(--olive)' }}>{item.sub}</span>}</span>
                  {item.members.length > 0 && item.kind !== 'user' && <span className="text-xs" style={{ color: 'var(--olive)' }}>{item.members.length} member{item.members.length === 1 ? '' : 's'}</span>}
                </button>); })}
            </div>); })}
        </div>
      )}
      <p className="text-xs mt-1" style={{ color: 'var(--olive)' }} data-testid={`${testId}-count`}>
        <b>{ids.length}</b> member{ids.length === 1 ? '' : 's'} ({withEmail} with email{ids.length - withEmail > 0 ? <span style={{ color: '#8a5a00' }}>, {ids.length - withEmail} without</span> : ''}){emails.length > 0 && <> · <b>{emails.length}</b> other address{emails.length === 1 ? '' : 'es'}</>}
        {(ids.length > 0 || emails.length > 0) && <button type="button" onClick={() => set([], [])} className="underline ml-2" data-testid={`${testId}-clear`}>clear</button>}
        {inactiveCount > 0 && (
          <label className="inline-flex items-center gap-1 ml-3 cursor-pointer" data-testid={`${testId}-include-inactive`} onClick={(e) => e.stopPropagation()}>
            <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} data-testid={`${testId}-include-inactive-checkbox`} /> include deceased / resigned / no-contact ({inactiveCount})
          </label>
        )}
      </p>
    </div>
  );
}
